// Server-side check-in domain logic (Phase 3). Pure, dependency-free TypeScript.
// No date libraries — wall-time/zone conversion uses the Intl API only.
// Consumes the canonical rules fixture (RUL-2) for every XP amount; no literals.

// The import attribute keeps this module loadable by Deno (Edge Functions
// import it relatively) as well as vitest/esbuild.
import fixture from "../guidelines-values.json" with { type: "json" };

export interface CheckinConfig {
  preKickoffMinutes: number;
  postFullTimeMinutes: number;
  gameStandardMinutes: number;
  impossibleJumpKm: number;
  impossibleJumpMinutes: number;
}

export type XpValues = typeof fixture;

export type XpSourceType = "team_first" | "team_repeat" | "venue_first" | "venue_repeat";

export interface XpAward {
  source_type: XpSourceType;
  subject_id: string;
  amount: number;
  season_year: number;
}

const EARTH_RADIUS_METERS = 6371000;
const MS_PER_MINUTE = 60_000;

/**
 * Great-circle distance in metres between two lat/lng points, using the
 * standard haversine formula and an earth radius of 6,371,000 m.
 */
export function haversineMeters(
  a: { lat: number; lng: number },
  b: { lat: number; lng: number },
): number {
  const toRad = (deg: number) => (deg * Math.PI) / 180;
  const dLat = toRad(b.lat - a.lat);
  const dLng = toRad(b.lng - a.lng);
  const lat1 = toRad(a.lat);
  const lat2 = toRad(b.lat);
  const h =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(lat1) * Math.cos(lat2) * Math.sin(dLng / 2) ** 2;
  return 2 * EARTH_RADIUS_METERS * Math.asin(Math.sqrt(h));
}

/**
 * Read the wall-clock year/month/day/hour/minute/second that an instant T
 * projects onto an IANA timezone, via Intl.DateTimeFormat. hour12:false gives
 * 24-hour; some engines emit "24" for midnight, so normalise to 0.
 */
function wallTimeInZone(
  t: Date,
  zone: string,
): { year: number; month: number; day: number; hour: number; minute: number; second: number } {
  const fmt = new Intl.DateTimeFormat("en-US", {
    timeZone: zone,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: false,
  });
  const parts: Record<string, string> = {};
  for (const p of fmt.formatToParts(t)) parts[p.type] = p.value;
  let hour = parseInt(parts.hour ?? "0", 10);
  if (hour === 24) hour = 0;
  return {
    year: parseInt(parts.year ?? "1970", 10),
    month: parseInt(parts.month ?? "1", 10),
    day: parseInt(parts.day ?? "1", 10),
    hour,
    minute: parseInt(parts.minute ?? "0", 10),
    second: parseInt(parts.second ?? "0", 10),
  };
}

/**
 * The offset (ms) that the timezone applies to UTC at instant T — i.e.
 * (zone-wall-time-treated-as-UTC) minus (actual UTC instant). Positive for
 * zones ahead of UTC (e.g. UTC+10 → +36_000_000).
 */
function zoneOffsetMs(t: Date, zone: string): number {
  const w = wallTimeInZone(t, zone);
  const zoneWallEpochMs = Date.UTC(w.year, w.month - 1, w.day, w.hour, w.minute, w.second);
  return zoneWallEpochMs - t.getTime();
}

/**
 * Validate that `match_date` is a strict YYYY-MM-DD string AND a real calendar
 * date (not a syntactically-plausible rollover like "2026-13-40" or
 * "2026-02-30"). Returns the numeric components on success, throws a
 * descriptive Error otherwise.
 */
function parseMatchDate(matchDate: unknown): { year: number; month: number; day: number } {
  if (typeof matchDate !== "string") {
    throw new Error(`invalid match_date: expected YYYY-MM-DD string, got ${String(matchDate)}`);
  }
  const m = matchDate.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!m) {
    throw new Error(`invalid match_date: "${matchDate}" does not match YYYY-MM-DD`);
  }
  const year = parseInt(m[1], 10);
  const month = parseInt(m[2], 10);
  const day = parseInt(m[3], 10);
  if (month < 1 || month > 12) {
    throw new Error(`invalid match_date: "${matchDate}" month out of range 01-12`);
  }
  if (day < 1 || day > 31) {
    throw new Error(`invalid match_date: "${matchDate}" day out of range 01-31`);
  }
  // Round-trip the components through Date.UTC to reject non-existent calendar
  // dates (e.g. Feb 30). Date.UTC silently rolls over invalid month/day into
  // the next valid date, so we compare the wall fields back.
  const probe = new Date(Date.UTC(year, month - 1, day));
  if (
    probe.getUTCFullYear() !== year ||
    probe.getUTCMonth() !== month - 1 ||
    probe.getUTCDate() !== day
  ) {
    throw new Error(`invalid match_date: "${matchDate}" is not a real calendar date`);
  }
  return { year, month, day };
}

/**
 * Validate that `kickoff_local` is HH:MM or HH:MM:SS with in-range fields.
 * Returns the numeric {hour, minute, second} on success, throws a descriptive
 * Error otherwise. Leading-zero two-digit fields are required so the canonical
 * fixture format is the only accepted shape.
 */
function parseKickoffLocal(kickoffLocal: unknown): { hour: number; minute: number; second: number } {
  if (typeof kickoffLocal !== "string") {
    throw new Error(`invalid kickoff_local: expected HH:MM[:SS] string, got ${String(kickoffLocal)}`);
  }
  const m = kickoffLocal.match(/^(\d{2}):(\d{2})(?::(\d{2}))?$/);
  if (!m) {
    throw new Error(`invalid kickoff_local: "${kickoffLocal}" does not match HH:MM or HH:MM:SS`);
  }
  const hour = parseInt(m[1], 10);
  const minute = parseInt(m[2], 10);
  const second = m[3] !== undefined ? parseInt(m[3], 10) : 0;
  if (hour > 23) {
    throw new Error(`invalid kickoff_local: "${kickoffLocal}" hour out of range 00-23`);
  }
  if (minute > 59) {
    throw new Error(`invalid kickoff_local: "${kickoffLocal}" minute out of range 00-59`);
  }
  if (second > 59) {
    throw new Error(`invalid kickoff_local: "${kickoffLocal}" second out of range 00-59`);
  }
  return { hour, minute, second };
}

/**
 * Validate that `venueTimezone` is an IANA zone name accepted by the platform
 * Intl API. Intl.DateTimeFormat throws a RangeError for an unknown zone; we
 * catch and rethrow as a descriptive Error so the Edge Function's generic
 * `catch { return 500 }` produces a reliable, labelled failure (SEC-12).
 */
function assertValidTimezone(venueTimezone: unknown): void {
  if (typeof venueTimezone !== "string" || venueTimezone.length === 0) {
    throw new Error(`invalid venue timezone: expected non-empty IANA name, got ${String(venueTimezone)}`);
  }
  try {
    // A probe formatter is enough — construction throws on an unknown zone.
    new Intl.DateTimeFormat("en-US", { timeZone: venueTimezone });
  } catch {
    throw new Error(`invalid venue timezone: "${venueTimezone}" is not a known IANA zone`);
  }
}

/**
 * Convert a fixture's stored local kickoff fields to the correct UTC instant,
 * DST-correct, using the offset-probe technique (no date libraries).
 *
 * Fixtures store `match_date` (YYYY-MM-DD) and `kickoff_local` (HH:MM[:SS])
 * local to the venue's IANA timezone. To get the UTC instant:
 *
 *  1. Build a candidate by interpreting the local wall time as if it were UTC
 *     (`Date.UTC(y, mo-1, d, h, mi, s)`).
 *  2. Read the timezone's offset at that candidate via Intl (zoneOffsetMs).
 *  3. Subtract that offset to get a corrected instant T1.
 *  4. Re-probe the offset at T1. If it matches step 2, we are done — the
 *     offset is stable around this instant. If it differs, the candidate sat
 *     on a DST transition; one further correction with the new offset resolves
 *     it (offsets only change by an hour and the corrected instant lands on
 *     one side of the transition, so a single extra probe is sufficient and
 *     convergent).
 *
 * Wall times that fall inside a DST spring-forward gap (which do not occur) or
 * a fall-back overlap (which occur twice) resolve deterministically to the
 * offset that applies at the corrected instant — the post-shift side for gaps
 * and the second occurrence for overlaps, per the offset at T1.
 *
 * Input validation (SEC-12 / codex MUST-FIX): a malformed `match_date`,
 * `kickoff_local`, or unknown IANA timezone is REJECTED with a descriptive
 * Error rather than silently normalised. Previously an invalid date/time
 * flowed through Date.UTC/Date.parse and produced a shifted-but-valid instant,
 * which at a reward boundary is a correctness hole — the Edge Function already
 * catches a throw here and returns 500, so a reliable throw closes the hole.
 */
export function fixtureKickoffUtc(
  matchDate: string,
  kickoffLocal: string,
  venueTimezone: string,
): Date {
  const { year, month, day } = parseMatchDate(matchDate);
  const { hour, minute, second } = parseKickoffLocal(kickoffLocal);
  assertValidTimezone(venueTimezone);

  const candidateWallMs = Date.UTC(year, month - 1, day, hour, minute, second);
  const t0 = new Date(candidateWallMs);
  const off0 = zoneOffsetMs(t0, venueTimezone);
  const t1 = new Date(candidateWallMs - off0);
  const off1 = zoneOffsetMs(t1, venueTimezone);
  if (off1 === off0) return t1;
  return new Date(candidateWallMs - off1);
}

/**
 * The check-in window for a fixture. Opens `preKickoffMinutes` before kickoff
 * and closes at kickoff + `gameStandardMinutes` + `postFullTimeMinutes`.
 *
 * `gameStandardMinutes` is a SCHEDULED-FULLTIME estimate (a rugby-league match
 * is 80 minutes of play plus halftime and ordinary stoppages). Phase 4 brings
 * a real fulltime signal from the stats feed; until then the close edge is an
 * estimate based on the standard scheduled duration, not the live clock.
 */
export function checkinWindow(
  kickoffUtc: Date,
  cfg: CheckinConfig,
): { opensAt: Date; closesAt: Date } {
  const opensAt = new Date(kickoffUtc.getTime() - cfg.preKickoffMinutes * MS_PER_MINUTE);
  const closesAt = new Date(
    kickoffUtc.getTime() +
      (cfg.gameStandardMinutes + cfg.postFullTimeMinutes) * MS_PER_MINUTE,
  );
  return { opensAt, closesAt };
}

/**
 * Whether `nowUtc` is within the check-in window. INCLUSIVE on both edges:
 * a check-in at exactly `opensAt` or exactly `closesAt` is accepted. This
 * matches the rule that a fan who arrives the instant the gate opens, or who
 * confirms at the scheduled-fulltime whistle, is still valid.
 */
export function isWithinWindow(
  nowUtc: Date,
  kickoffUtc: Date,
  cfg: CheckinConfig,
): boolean {
  const { opensAt, closesAt } = checkinWindow(kickoffUtc, cfg);
  return nowUtc.getTime() >= opensAt.getTime() && nowUtc.getTime() <= closesAt.getTime();
}

/**
 * SEC-10 anti-spoof guard. Returns true when the jump from `prev` to `next` is
 * implausibly fast: distance strictly greater than `impossibleJumpKm` AND time
 * gap strictly less than `impossibleJumpMinutes`. Both bounds are strict, so a
 * journey of exactly `impossibleJumpKm` in exactly `impossibleJumpMinutes` is
 * NOT flagged (the boundary belongs to the plausible side). `next.atUtc` must
 * be >= `prev.atUtc`; a zero or negative gap is treated as impossible (0 < cfg
 * minutes, so the time condition is satisfied) and the distance condition
 * decides.
 */
export function isImpossibleJump(
  prev: { lat: number; lng: number; atUtc: Date },
  next: { lat: number; lng: number; atUtc: Date },
  cfg: CheckinConfig,
): boolean {
  const distanceKm = haversineMeters(prev, next) / 1000;
  const gapMinutes = (next.atUtc.getTime() - prev.atUtc.getTime()) / MS_PER_MINUTE;
  return distanceKm > cfg.impossibleJumpKm && gapMinutes < cfg.impossibleJumpMinutes;
}

/**
 * XP awards for a check-in: one award per team (home AND away) and one for the
 * venue. Each is "first" when its id is not in the corresponding seen-this-season
 * array, else "repeat". Amounts come ONLY from the rules fixture's `xp` block
 * (RUL-2) — no literals in this function.
 *
 * Nullable / duplicate team ids (codex MUST-FIX, GLM note). Fixtures can carry
 * nullable team FKs (State of Origin / finals / byes), and bad data could in
 * theory make home equal away. Contract:
 *
 *  - Skip any `null`/`undefined` team id — no award is emitted and no row with
 *    a `null` `subject_id` is produced (a nullable FK means "no team side
 *    applies", e.g. a bye).
 *  - If `homeTeamId === awayTeamId` (both present), emit exactly ONE team award
 *    for that id, not two — the fan saw one team side, not two.
 *  - The venue award is ALWAYS emitted. A venue is mandatory for a check-in;
 *    a `null`/`undefined` `venueId` is a contract violation and the function
 *    throws a descriptive Error rather than emitting a `null`-subject row.
 *
 * The optional `values` parameter defaults to the imported canonical fixture
 * and exists so tests can inject a mutated copy and prove outputs track the
 * fixture (round-1 accepted-risk closure). Production callers omit it.
 */
export function checkinXpAwards(
  input: {
    teamIdsSeenThisSeason: string[];
    venueIdsSeenThisSeason: string[];
    fixture: { homeTeamId: string | null | undefined; awayTeamId: string | null | undefined; venueId: string | null | undefined };
    seasonYear: number;
  },
  values: XpValues = fixture,
): XpAward[] {
  const { teamIdsSeenThisSeason, venueIdsSeenThisSeason, fixture: fx, seasonYear } = input;
  const seenTeams = new Set(teamIdsSeenThisSeason);
  const seenVenues = new Set(venueIdsSeenThisSeason);

  if (fx.venueId === null || fx.venueId === undefined) {
    throw new Error("invalid check-in fixture: venueId is null/undefined (a venue is mandatory for a check-in)");
  }

  const awards: XpAward[] = [];

  // Deduplicate and drop null/undefined team ids. Insertion order is preserved
  // (home first, then away) so the emitted award order is stable; when home
  // equals away the Set collapses the two to a single award.
  const teamIds = new Set<string>();
  for (const id of [fx.homeTeamId, fx.awayTeamId]) {
    if (id === null || id === undefined) continue;
    teamIds.add(id);
  }
  for (const teamId of teamIds) {
    const first = !seenTeams.has(teamId);
    awards.push({
      source_type: first ? "team_first" : "team_repeat",
      subject_id: teamId,
      amount: first ? values.xp.team.first : values.xp.team.repeat,
      season_year: seasonYear,
    });
  }

  const venueFirst = !seenVenues.has(fx.venueId);
  awards.push({
    source_type: venueFirst ? "venue_first" : "venue_repeat",
    subject_id: fx.venueId,
    amount: venueFirst ? values.xp.venue.first : values.xp.venue.repeat,
    season_year: seasonYear,
  });

  return awards;
}
